import {DatePickerMode, DatePickerValue} from "@/components/datePicker/DatePicker.types.ts";
import {parseInputForDate} from "@/components/datePicker/DatePicker.utils.ts";
import {DatePickerInputProps} from "@/components/datePicker/subComponents/DatePickerInput/DatePickerInput.types.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {Input} from "@/components/input/Input.tsx";
import {InputGroup} from "@/components/inputGroup/InputGroup.tsx";
import React, {forwardRef} from "react";
import {DateRange} from "react-day-picker";
import {FiCalendar} from "react-icons/fi";

export const DatePickerInput = forwardRef(
  <Mode extends DatePickerMode = "range">(
    {
      placeholder = "Select...",
      mode,
      splitRole,
      currentDate,
      onChange,
      className,
      timezone,
      error,
      setError,
      ...rest
    }: DatePickerInputProps<Mode>,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      let date = parseInputForDate(e.target.value, mode, timezone);

      // if the date or either of the range dates is invalid, set error flag and return undefined or partially undefined range
      if (
        (date instanceof Date && isNaN(date.getTime())) ||
        (date instanceof Object && "to" in date && date.to && isNaN(date.to.getTime())) ||
        (date instanceof Object && "from" in date && date.from && isNaN(date.from.getTime()))
      ) {
        if (date instanceof Object) {
          let from = undefined;
          let to = undefined;
          if ("from" in date && date.from && !isNaN(date.from.getTime())) {
            from = date.from;
          }
          if ("to" in date && date.to && !isNaN(date.to.getTime())) {
            to = date.to;
          }
          date = from || to ? {from, to} : undefined;
        } else {
          date = undefined;
        }
        onChange?.(e, e.target.value, date as DatePickerValue<Mode>);
        setError("Invalid date");
        return;
      }

      setError(null);
      if (mode === "split") {
        const previousDate = currentDate as DateRange | undefined;
        if (splitRole === "start") {
          date = {from: date as Date | undefined, to: previousDate?.to} as DateRange;
        } else if (splitRole === "end") {
          date = {from: previousDate?.from, to: date as Date | undefined} as DateRange;
        }
      }
      onChange?.(e, e.target.value, date as DatePickerValue<Mode>);
    };

    return (
      <>
        <InputGroup
          endElement={<Icon icon={FiCalendar} />}
          className={className}
          aria-invalid={rest["aria-invalid"] || !!error}
        >
          <Input ref={ref} placeholder={placeholder} onChange={onChangeHandler} {...rest} />
        </InputGroup>
      </>
    );
  }
);
